/**
 * Spec 035 v0.4: the sheets of a unit — a new one, a change of it (name, kind, place in the tree,
 * head, staff, visiting card) and placing a card from «Без отдела». Every change is a PATCH with
 * the version read; a 409 reloads the units to show them as they are now.
 */

import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, newIdempotencyKey } from "@/api/client";
import { Departments, type DepartmentOut, type UnitKind, type UnitMemberIn, type UnitService, type UnplacedAgentOut } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { useToast } from "@/components/ui/Toast";
import { Caption } from "@/kit";
import { KIND_LABEL, KINDS, MAX_DEPTH, parentChoices, pathLabel, staffOf } from "@/lib/units";

const refused = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408;

const KIND_OPTIONS = KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }));

/** The cards a unit may take: its own and those in «Без отдела». */
function candidates(unit: DepartmentOut | null, unplaced: UnplacedAgentOut[]) {
  const own = unit ? unit.members.map((m) => ({ id: m.agent_id, name: m.name })) : [];
  const free = unplaced.map((a) => ({ id: a.agent_id, name: a.name }));
  return [...own, ...free].sort((a, b) => a.name.localeCompare(b.name, "ru"));
}

/** A new unit (`unit` null, optionally inside `parentId`) or a change of one. */
export function UnitSheet({ open, onClose, unit = null, parentId = null, onSaved }: {
  open: boolean;
  onClose: () => void;
  unit?: DepartmentOut | null;
  parentId?: string | null;
  onSaved: (saved: DepartmentOut) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const units = useQuery({ queryKey: ["departments", "list", false], queryFn: () => Departments.list(), enabled: open });
  const unplaced = useQuery({ queryKey: ["departments", "unplaced"], queryFn: Departments.unplaced, enabled: open });
  const [name, setName] = useState(unit?.name ?? "");
  const [purpose, setPurpose] = useState(unit?.purpose_md ?? "");
  const [kind, setKind] = useState<UnitKind>(unit?.kind ?? (parentId ? "department" : "division"));
  const [parent, setParent] = useState<string>(unit ? unit.parent_id ?? "" : parentId ?? "");
  const [head, setHead] = useState<string>(unit?.head?.agent_id ?? "");
  const [staff, setStaff] = useState<string[]>(unit ? staffOf(unit).map((m) => m.agent_id) : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const attempt = useRef<string | null>(null);

  const people = useMemo(() => candidates(unit, unplaced.data ?? []), [unit, unplaced.data]);
  const parents = useMemo(() => parentChoices(units.data ?? [], unit?.id ?? null), [units.data, unit?.id]);

  const members: UnitMemberIn[] = [
    ...(head ? [{ agent_id: head, role: "leader" as const }] : []),
    ...staff.filter((id) => id !== head).map((id) => ({ agent_id: id, role: "group" as const })),
  ];

  const save = async () => {
    setError(undefined);
    setSaving(true);
    try {
      let saved: DepartmentOut;
      if (unit) {
        saved = await Departments.update(unit.id, {
          version: unit.version,
          ...(name.trim() !== unit.name ? { name: name.trim() } : {}),
          ...((purpose.trim() || null) !== (unit.purpose_md ?? null) ? { purpose_md: purpose.trim() || null } : {}),
          ...(kind !== unit.kind ? { kind } : {}),
          ...((parent || null) !== unit.parent_id ? { parent_id: parent || null } : {}),
          members,
        });
      } else {
        const key = (attempt.current ??= newIdempotencyKey());
        saved = await Departments.create(
          { name: name.trim(), purpose_md: purpose.trim() || null, kind, parent_id: parent || null, members },
          key,
        );
        attempt.current = null;
      }
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(unit ? "Подразделение сохранено" : `«${saved.name}» заведено`);
      onSaved(saved);
    } catch (e) {
      if (refused(e)) attempt.current = null;
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["departments"] });
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Нажми ещё раз — второе подразделение не появится.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStaff = (id: string) =>
    setStaff((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={unit ? `Изменить «${unit.name}»` : "Новое подразделение"}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Сохраняем…" disabled={!name.trim()} onClick={() => void save()}>
            {unit ? "Сохранить" : "Завести"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField label="Название" value={name} maxLength={120}
          onChange={(e) => { attempt.current = null; setName(e.target.value); }} />
        <Segmented label="Вид — только подпись" options={KIND_OPTIONS} value={kind}
          onChange={(v) => { attempt.current = null; setKind(v); }} />
        <SelectField
          label="Внутри"
          hint={`Пусто — верхнее подразделение: с ним говорят люди. Не глубже ${MAX_DEPTH} уровней.`}
          value={parent}
          placeholder="Верхнее подразделение"
          options={parents.map((u) => ({ value: u.id, label: pathLabel(u) }))}
          onChange={(v) => { attempt.current = null; setParent(v); }}
        />
        <TextArea label="Зачем" value={purpose} rows={3} maxLength={5000}
          onChange={(e) => { attempt.current = null; setPurpose(e.target.value); }} />
        <SelectField
          label="Руководитель"
          hint="Без руководителя подразделение не принимает поручений"
          value={head}
          placeholder="Нет руководителя"
          options={people.map((p) => ({ value: p.id, label: p.name }))}
          onChange={(v) => { attempt.current = null; setHead(v); setStaff((now) => now.filter((x) => x !== v)); }}
        />
        <div className="flex flex-col gap-2">
          <Caption>Сотрудники</Caption>
          {people.filter((p) => p.id !== head).length === 0 && (
            <p className="text-meta text-p-text-muted">Свободных агентов нет: все уже в подразделениях.</p>
          )}
          {people.filter((p) => p.id !== head).map((p) => (
            <label key={p.id} className="flex min-h-11 items-center gap-3 text-body text-p-text">
              <input type="checkbox" className="h-5 w-5" checked={staff.includes(p.id)}
                onChange={() => { attempt.current = null; toggleStaff(p.id); }} />
              {p.name}
            </label>
          ))}
          <p className="text-meta text-p-text-muted">Агент работает в одном месте: руководителем или сотрудником одного подразделения.</p>
        </div>
      </div>
    </Sheet>
  );
}

/** Puts a card from «Без отдела» into a unit — as its head when it has none, or as staff. */
export function PlaceAgentSheet({ agent, onClose }: { agent: UnplacedAgentOut | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const units = useQuery({ queryKey: ["departments", "list", false], queryFn: () => Departments.list(), enabled: Boolean(agent) });
  const [target, setTarget] = useState("");
  const [asHead, setAsHead] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const unit = units.data?.find((u) => u.id === target) ?? null;
  const headFree = Boolean(unit && !unit.head);

  const save = async () => {
    if (!agent || !unit) return;
    setError(undefined);
    setSaving(true);
    try {
      const members: UnitMemberIn[] = [
        ...unit.members.map((m) => ({ agent_id: m.agent_id, role: m.role })),
        { agent_id: agent.agent_id, role: asHead && headFree ? "leader" : "group" },
      ];
      await Departments.update(unit.id, { version: unit.version, members });
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(`«${agent.name}» теперь в «${unit.name}»`);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["departments"] });
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Попробуй ещё раз.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open={Boolean(agent)}
      onClose={onClose}
      title={agent ? `Куда поставить «${agent.name}»` : ""}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Ставим…" disabled={!unit} onClick={() => void save()}>
            Поставить
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectField
          label="Подразделение"
          value={target}
          placeholder="Выбери подразделение"
          options={(units.data ?? []).map((u) => ({ value: u.id, label: pathLabel(u) }))}
          onChange={(v) => { setTarget(v); setAsHead(false); }}
        />
        {unit && (
          <Segmented
            label="Кем"
            options={[
              { value: "staff", label: "Сотрудником" },
              ...(headFree ? [{ value: "head", label: "Руководителем" }] : []),
            ]}
            value={asHead && headFree ? "head" : "staff"}
            onChange={(v) => setAsHead(v === "head")}
          />
        )}
        {unit && !headFree && unit.head && (
          <p className="text-meta text-p-text-muted">Руководитель уже есть: {unit.head.name}.</p>
        )}
      </div>
    </Sheet>
  );
}

/** The visiting card: what a unit takes, what comes in and out, who of it does the work. */
export function ServicesSheet({ unit, onClose }: { unit: DepartmentOut | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<UnitService[]>(() =>
    (unit?.services ?? []).map(({ key, title, input_md, output_md, agent_id }) => ({ key, title, input_md, output_md, agent_id })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  if (!unit) return null;
  const doers = unit.members.map((m) => ({ value: m.agent_id, label: m.role === "leader" ? `${m.name} (руководитель)` : m.name }));

  const set = (index: number, patch: Partial<UnitService>) =>
    setRows((now) => now.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const add = () =>
    setRows((now) => [...now, { key: `service-${now.length + 1}`, title: "", input_md: null, output_md: null, agent_id: null }]);

  const save = async () => {
    setError(undefined);
    setSaving(true);
    try {
      const services = rows
        .filter((r) => r.title.trim())
        .map((r) => ({ ...r, title: r.title.trim(), input_md: r.input_md?.trim() || null, output_md: r.output_md?.trim() || null }));
      await Departments.update(unit.id, { version: unit.version, services });
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success("Визитка сохранена");
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["departments"] });
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Попробуй ещё раз.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Визитка «${unit.name}»`}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Сохраняем…" onClick={() => void save()}>
            Сохранить
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-meta text-p-text-muted">
          Услуги, которые подразделение берёт у других: поручение на услугу идёт сразу исполнителю, его руководитель это видит.
        </p>
        {rows.map((r, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-card border border-p-border p-3">
            <TextField label="Услуга" value={r.title} maxLength={120} onChange={(e) => set(i, { title: e.target.value })} />
            <TextField label="Ключ (латиницей)" value={r.key} maxLength={60}
              onChange={(e) => set(i, { key: e.target.value.toLowerCase() })} />
            <TextArea label="Что на входе" rows={2} value={r.input_md ?? ""} maxLength={2000}
              onChange={(e) => set(i, { input_md: e.target.value })} />
            <TextArea label="Что на выходе" rows={2} value={r.output_md ?? ""} maxLength={2000}
              onChange={(e) => set(i, { output_md: e.target.value })} />
            <SelectField label="Кто делает" value={r.agent_id ?? ""} placeholder="Руководитель решает сам"
              options={doers} onChange={(v) => set(i, { agent_id: v || null })} />
            <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-blocked"
              onClick={() => setRows((now) => now.filter((_, j) => j !== i))}>
              Убрать услугу
            </button>
          </div>
        ))}
        <Button variant="secondary" size="compact" onClick={add}>Добавить услугу</Button>
      </div>
    </Sheet>
  );
}
